import React, { useMemo } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { showSignInModal } from 'redux/actions/modalActions';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { store } from 'react-notifications-component';
import { Share2 } from 'lucide-react';
import {
  REQUIRED_SIGNIN_FOR_CHECKOUT,
  REQUIRED_SIGNIN_FOR_SAVEFORLATER,
} from 'consts/form';

import { getPresetSubTotalPrice, getTotalQuantity } from 'utils/preset.util';
import SaveForLaterModal from './SaveForLaterModal';
import CheckoutInfo from './CheckoutInfo';
import GrandTotalPrice from './TotalPrice';
import { useLocation, useParams } from 'react-router-dom';
import { priceDecimalFormatter } from 'formatter/preset.formatter';

function filteredCheckoutData(checkoutData, menuItems) {
  const allMenuIds = menuItems?.map(menu => menu.id) || [];

  return checkoutData?.filter(item => allMenuIds.includes(item.menuId));
}

const secondaryButtonClass =
  'w-full py-3 px-4 rounded-xl border border-gray-200 font-heading font-bold text-sm text-[#202124] hover:border-[#E53935] hover:text-[#E53935] transition-all mt-3';

function OrderDetails({
  setCurrentPresetItemFromCheckoutData,
  createGroupOrderForPreset,
  setShowSaveForLaterModal,
  showMinHourNoticeError,
  showSaveForLaterModal,
  deliveryPriceCents,
  removeCheckoutItem,
  cutleryFeesInCents,
  preOrderNoticeHour,
  saveCustomPreset,
  onCheckoutClick,
  checkoutData,
  canShareLink,
  canSaveUpdate,
  typeDetails,
  showAddondata,
  cutleryNeeded,
  cutleryCount,
  cutleryFees,
  buttons,
  preset,
}) {
  const dispatch = useDispatch();
  const userSignedIn = useSelector(state => get(state, 'user.user.confirmed'));
  const receivingMethod = useSelector(state => state.receivingMethod);
  const title = showAddondata ? `Submit Addon Order` : 'Checkout';
  const { chefs, deliveryFeeData } = preset;
  const showGroupOrderButton = !!(chefs && chefs.length === 1);

  // const menuItems = chefs?.flatMap(chef => chef.menus) || [];

  const menuItems =
    chefs?.flatMap(chef =>
      chef.menus?.filter(menu => menu?.isDeleted !== true),
    ) || [];

  const validCheckoutData = useMemo(() => {
    if (!typeDetails) return checkoutData;
    return filteredCheckoutData(checkoutData, menuItems);
  }, [typeDetails, checkoutData, menuItems]);

  const subTotal = getPresetSubTotalPrice(validCheckoutData);
  const savedForLater = preset.presetType === 'saved_for_later';
  const publicPreset = !savedForLater;
  const percentValue = priceDecimalFormatter(subTotal * preset.taxRate) * 100;
  const totalPriceCents = showAddondata
    ? subTotal + percentValue
    : receivingMethod === 'delivery'
    ? (subTotal + deliveryPriceCents + (cutleryFeesInCents || 0)) *
      (1 + (preset?.taxRate || 0))
    : subTotal * (1 + preset.taxRate);

  const totalQuantity = useMemo(() => {
    return getTotalQuantity(validCheckoutData);
  }, [checkoutData, menuItems]);

  const copyToClipboard = (
    <CopyToClipboard text={preset.sharedUrl}>
      <button
        className={`${secondaryButtonClass} flex items-center justify-center gap-2`}
        type="button"
        onClick={() => {
          store.addNotification({
            title: 'URL Copied!',
            message: 'You can now paste the URL',
            type: 'success',
            insert: 'top',
            container: 'top-right',
            animationIn: ['animated', 'bounceIn'],
            animationOut: ['animated', 'fadeOut'],
            dismiss: {
              duration: 4000,
            },
          });
        }}
      >
        <Share2 className="w-4 h-4" />
        Share This
      </button>
    </CopyToClipboard>
  );

  return (
    <div
      className="bg-white rounded-2xl md:rounded-3xl border border-gray-200/80 shadow-sm overflow-hidden"
      id="sourceOrderDetails"
    >
      <div className="p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-panda-tint/50 to-white">
        <span className="font-heading font-bold text-lg text-[#202124]">
          Order Details
        </span>
        {totalQuantity > 0 && (
          <span className="w-6 h-6 rounded-full bg-[#E53935] text-white text-xs font-bold flex items-center justify-center">
            {totalQuantity}
          </span>
        )}
      </div>

      <CheckoutInfo
        {...{
          preset,
          cutleryFees,
          checkoutData: validCheckoutData,
          cutleryCount,
          showAddondata,
          cutleryNeeded,
          tooltipData: deliveryFeeData?.ranges?.join('\n') || '',
          deliveryPriceCents,
          cutleryFeesInCents,
          removeCheckoutItem,
          preOrderNoticeHour,
          showMinHourNoticeError,
          setCurrentPresetItemFromCheckoutData,
        }}
      />

      <div className="p-4 sm:p-5 border-t border-gray-100 bg-panda-tint/30">
        <div className="flex justify-between items-center text-base font-bold text-[#202124]">
          <span>Total Price</span>
          {showAddondata ? (
            <span className="text-[#E53935] font-brand text-xl">
              <GrandTotalPrice totalPriceCents={totalPriceCents || 0} />
            </span>
          ) : (
            <span className="text-[#E53935] font-brand text-xl">
              <GrandTotalPrice
                totalPriceCents={subTotal ? totalPriceCents : 0}
              />
            </span>
          )}
        </div>

        {userSignedIn ? (
          <>
            {buttons}
            {typeDetails && (
              <button
                className="w-full py-3.5 px-4 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] text-white font-heading font-bold text-sm shadow-md shadow-red-500/20 active:scale-98 transition-all mt-3"
                type="button"
                onClick={onCheckoutClick}
              >
                <span className="hide-mobile">{title}</span>
                <span className="hide-desktop">Pay</span>
              </button>
            )}
            {canShareLink && typeDetails && copyToClipboard}
            {publicPreset && canSaveUpdate && typeDetails && (
              <button
                className={secondaryButtonClass}
                type="button"
                onClick={() => setShowSaveForLaterModal(true)}
              >
                Save Cart
              </button>
            )}
            {showGroupOrderButton && typeDetails && chefs?.[0]?.allowGroupOrders && (
              <button
                className={secondaryButtonClass}
                type="button"
                onClick={() => createGroupOrderForPreset(preset.id)}
              >
                Create Corporate Order
              </button>
            )}
          </>
        ) : (
          <>
            <button
              className="w-full py-3.5 px-4 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] text-white font-heading font-bold text-sm shadow-md shadow-red-500/20 active:scale-98 transition-all mt-3"
              type="button"
              onClick={() =>
                dispatch(showSignInModal(REQUIRED_SIGNIN_FOR_CHECKOUT))
              }
            >
              <span className="hide-mobile">{title}</span>
              <span className="hide-desktop">Pay</span>
            </button>
            {canShareLink && copyToClipboard}
            {canSaveUpdate && (
              <button
                className={secondaryButtonClass}
                type="button"
                onClick={() =>
                  dispatch(showSignInModal(REQUIRED_SIGNIN_FOR_SAVEFORLATER))
                }
              >
                Save Cart
              </button>
            )}
            {showGroupOrderButton && (
              <button
                className={secondaryButtonClass}
                type="button"
                onClick={() =>
                  dispatch(showSignInModal(REQUIRED_SIGNIN_FOR_SAVEFORLATER))
                }
              >
                Create Corporate Order
              </button>
            )}
          </>
        )}

        <SaveForLaterModal
          show={showSaveForLaterModal}
          onClose={() => setShowSaveForLaterModal(false)}
          onSubmit={saveCustomPreset}
        />
      </div>
    </div>
  );
}
OrderDetails.propTypes = {
  preset: PropTypes.shape(),
  checkoutData: PropTypes.arrayOf(PropTypes.shape({})),
  deliveryPriceCents: PropTypes.number,
  saveCustomPreset: PropTypes.func,
  createGroupOrderForPreset: PropTypes.func,
  setCurrentPresetItemFromCheckoutData: PropTypes.func,
  removeCheckoutItem: PropTypes.func,
  onCheckoutClick: PropTypes.func,
  setShowSaveForLaterModal: PropTypes.func,
  showSaveForLaterModal: PropTypes.bool,
  preOrderNoticeHour: PropTypes.number,
  showMinHourNoticeError: PropTypes.bool,
  canShareLink: PropTypes.bool,
  canSaveUpdate: PropTypes.bool,
  typeDetails: PropTypes.bool,
  buttons: PropTypes.arrayOf(),
  showAddondata: PropTypes.bool,
  cutleryNeeded: PropTypes.bool,
  cutleryCount: PropTypes.number,
  cutleryFeesInCents: PropTypes.number,
  cutleryFees: PropTypes.number,
};

OrderDetails.defaultProps = {
  preset: {},
  checkoutData: [],
  deliveryPriceCents: 0,
  preOrderNoticeHour: 0,
  showMinHourNoticeError: false,
  onCheckoutClick: () => {},
  saveCustomPreset: () => {},
  createGroupOrderForPreset: () => {},
  setCurrentPresetItemFromCheckoutData: () => {},
  removeCheckoutItem: () => {},
  setShowSaveForLaterModal: () => {},
  canShareLink: true,
  canSaveUpdate: true,
  typeDetails: true,
  showSaveForLaterModal: false,
  buttons: [],
  showAddondata: false,
  cutleryNeeded: false,
  cutleryCount: 0,
  cutleryFeesInCents: 0,
  cutleryFees: 0,
};

export default OrderDetails;
